import { useState } from "react";
import { Plus, Layers, Pencil, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCommands, Command } from "@/hooks/useCommands";
import { BatchCommandDialog } from "@/components/commands/BatchCommandDialog";
import { toast } from "sonner";

export function CommandManagementCard() {
  const { commands, isLoading, createCommand, createBatchCommands, updateCommand, deleteCommand } = useCommands();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [number, setNumber] = useState("");
  const [serviceTaxEnabled, setServiceTaxEnabled] = useState(true);

  const resetForm = () => {
    setNumber("");
    setServiceTaxEnabled(true);
  };

  const handleAdd = async () => {
    if (!number.trim()) {
      toast.error("Informe o número da comanda");
      return;
    }

    setIsSubmitting(true);
    const result = await createCommand({ number: number.trim() });
    
    if (result) {
      // Update service tax if needed
      if (!serviceTaxEnabled) {
        await updateCommand(result.id, { service_tax_enabled: false });
      }
      resetForm();
      setShowAddDialog(false);
    }
    setIsSubmitting(false);
  };

  const handleEdit = async () => {
    if (!selectedCommand || !number.trim()) return;

    setIsSubmitting(true);
    const result = await updateCommand(selectedCommand.id, {
      number: number.trim(),
      service_tax_enabled: serviceTaxEnabled,
    });
    setIsSubmitting(false);

    if (result) {
      resetForm();
      setShowEditDialog(false);
      setSelectedCommand(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedCommand) return;

    setIsSubmitting(true);
    await deleteCommand(selectedCommand.id);
    setIsSubmitting(false);
    setShowDeleteDialog(false);
    setSelectedCommand(null);
  };

  const handleToggleServiceTax = async (command: Command) => {
    await updateCommand(command.id, {
      service_tax_enabled: !command.service_tax_enabled,
    });
  };

  const openEditDialog = (command: Command) => {
    setSelectedCommand(command);
    setNumber(command.number);
    setServiceTaxEnabled(command.service_tax_enabled ?? true);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (command: Command) => {
    setSelectedCommand(command);
    setShowDeleteDialog(true);
  };

  const activeCommands = commands.filter(c => c.is_active);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Gerenciamento de Comandas</CardTitle>
              <CardDescription>
                Cadastre comandas individualmente ou em lote
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowBatchDialog(true)}>
                <Layers className="h-4 w-4 mr-2" />
                Em Lote
              </Button>
              <Button size="sm" onClick={() => { resetForm(); setShowAddDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Comanda
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : activeCommands.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma comanda cadastrada
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead className="text-center">Taxa 10%</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeCommands.map((command) => (
                    <TableRow key={command.id}>
                      <TableCell className="font-medium">{command.number}</TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={command.service_tax_enabled ?? true}
                          onCheckedChange={() => handleToggleServiceTax(command)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(command)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(command)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Comanda</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número *</Label>
              <Input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="001"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Aplicar Taxa de Serviço</Label>
              <Switch
                checked={serviceTaxEnabled}
                onCheckedChange={setServiceTaxEnabled}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Criar Comanda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Comanda</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número *</Label>
              <Input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Aplicar Taxa de Serviço</Label>
              <Switch
                checked={serviceTaxEnabled}
                onCheckedChange={setServiceTaxEnabled}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Comanda</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover a comanda {selectedCommand?.number}? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Dialog */}
      <BatchCommandDialog
        open={showBatchDialog}
        onOpenChange={setShowBatchDialog}
        onSubmit={createBatchCommands}
      />
    </>
  );
}
